import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import { SafeIcon, MobileIcon, IntegrationIcon } from '@segment/matcha/components/Icons/Brandbook'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import PowerYourAppsImage from './PowerYourAppsImage'

const copy = {
  heading: `Power all your apps with the same data`,
  description: `Integrate your data to ${process.env.INTEGRATION_COUNT}+ apps with just a few clicks.`,
}

const link = {
  text: `Explore Tracking API`,
  url: `https://segment.com/docs/connections/spec/track/`,
}

const features = [
  {
    heading: `Activate any tool instantly`,
    description: `Integrate new tools in minutes, not weeks. No more setup headaches, no need to push code.`,
    icon: <IntegrationIcon />,
  },
  {
    heading: `Make your mobile integrations agile`,
    description: `No more waiting for App Store approval. Turn on new tools instantly, even ones without their own mobile SDK.`,
    icon: <MobileIcon />,
  },
  {
    heading: `Remove vendor lock-in`,
    description: `Segment simplifies trying new solutions, eliminating expensive integration barriers to using top tools.`,
    icon: <SafeIcon />,
  },
]

const PowerYourApps: FC<PropsWithChildren<unknown>> = () => {
  return (
    <Wrapper>
      <SectionBase variant="2Col" copyVariant="narrow">
        <Section.Copy>
          <CopyTextBase>
            <CopyText.Heading tag="h2" variant="h2">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
            <CopyText.Link href={link.url}>{link.text}</CopyText.Link>
          </CopyTextBase>

          <ColumnTrackWrapper>
            <ColumnTrack gap="small">
              {features.map((feature) => (
                <FeatureBoxComponent key={feature.heading} alignment="left" headerPosition="left">
                  <FeatureBox.Header>
                    <IconWrapper elevation={1} variant="circle">
                      {feature.icon}
                    </IconWrapper>
                  </FeatureBox.Header>
                  <FeatureBox.Body>
                    <FeatureBox.Title>{feature.heading}</FeatureBox.Title>
                    <FeatureBox.Text>{feature.description}</FeatureBox.Text>
                  </FeatureBox.Body>
                </FeatureBoxComponent>
              ))}
            </ColumnTrack>
          </ColumnTrackWrapper>
        </Section.Copy>

        <Section.Contents>
          <PowerYourAppsImage />
        </Section.Contents>
      </SectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  display: grid;
`

const FeatureBoxComponent = styled(FeatureBoxBase)`
  ${({ theme }) => theme.breakpoints.between(`sm`, `md`)} {
    ${FeatureBox.CSSHeaderPositionTop}
  }
`

const ColumnTrackWrapper = styled.div`
  margin-top: 40px;
`

export default PowerYourApps
